import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Award, Mail, Lock, User, ArrowRight, Crown, Handshake, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  {
    value: 'public',
    label: 'Public Member',
    description: 'Browse awards and rate your favorites (15% weight)',
    icon: Users,
    color: 'text-blue-400'
  },
  {
    value: 'jury',
    label: 'Jury Member',
    description: 'Professional Jury - rate awards (30% weight)',
    icon: Crown,
    color: 'text-[#D4AF37]'
  },
  {
    value: 'partner',
    label: 'Partner',
    description: 'Hotels/Resorts — manage your own listings (no voting access)',
    icon: Handshake,
    color: 'text-green-400'
  },
  {
    value: 'professional_partner',
    label: 'Professional Partner',
    description: 'Industry professionals — rate awards (20% weight)',
    icon: Handshake,
    color: 'text-[#D4AF37]'
  }
];

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requestedRole, setRequestedRole] = useState('public');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        name,
        requested_role: requestedRole
      });
      setSuccess(true);
      toast.success('Registration submitted! Awaiting admin approval.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      setError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Award className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-semibold text-white mb-4">
            Registration Submitted!
          </h1>
          <p className="text-white/60 mb-2">
            Your account is pending admin approval.
          </p>
          <p className="text-white/40 text-sm mb-6">
            You requested: <span className="text-[#D4AF37] capitalize">{requestedRole}</span> membership
          </p>
          <p className="text-white/50 text-sm mb-8">
            You'll be able to login once your account is approved by an administrator.
          </p>
          <Link to="/login">
            <Button className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]">
              Go to Login
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
              <Award className="w-7 h-7 text-[#0A0A0A]" />
            </div>
          </Link>
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-white mb-2">
            Join Travel Awards
          </h1>
          <p className="text-white/60">
            Create an account to participate
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#121212] rounded-2xl border border-white/5 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div 
                className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="pl-12 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-12 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="pl-12 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/70">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="pl-12 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-3">
              <Label className="text-white/70">
                Membership Type
              </Label>
              <div className="space-y-2">
                {ROLE_OPTIONS.map((role) => {
                  const Icon = role.icon;
                  return (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => setRequestedRole(role.value)}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        requestedRole === role.value
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-white/10 bg-[#0A0A0A] hover:border-white/20'
                      }`}
                      data-testid={`role-${role.value}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 ${role.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${
                            requestedRole === role.value ? 'text-[#D4AF37]' : 'text-white'
                          }`}>
                            {role.label}
                          </p>
                          <p className="text-white/50 text-sm mt-0.5">
                            {role.description}
                          </p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          requestedRole === role.value
                            ? 'border-[#D4AF37] bg-[#D4AF37]'
                            : 'border-white/30'
                        }`}>
                          {requestedRole === role.value && (
                            <div className="w-2 h-2 rounded-full bg-[#0A0A0A]" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-white/40 text-xs">
                All registrations require admin approval
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] font-semibold"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Submit Registration
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#D4AF37] hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
